<docs>
A pill / tag / chip label, optionally with a leading icon and a dismiss × icon. The default slot is
the label. Purely a display chip: for a selectable set of pills (single/multi toggle) use
ZonePillGroup.

## Notes

- Icons scale with the pill: the leading icon is 12px at `sm` and 18px at `md`/`lg` (an 18px glyph
  overflows the 16px-tall sm pill). The dismiss × is 12px at every size.
- Per-colour text shades are tuned for contrast and are not a single shade across the palette
  (filled green is green-800 for the label but green-600 for the ×). Change them in
  FILLED_CLASSES / OUTLINED_CLASSES / CLOSE_ICON_SHADE together, not piecemeal; spec/Pill.spec.ts
  asserts the tables 1:1 against Figma.
- Figma draws the dismissible set for primary/gray/red/yellow/green only; this component accepts
  `dismissible` on all ten colours.
- Known Figma drift, resolved in favour of the read-only grid: filled gray is gray-600 there but
  gray-800 in the dismissible grid. The guidelines page also says small pills carry no icon, yet the
  newer read-only Small variants ship a 12px one; this component follows the variants and allows it.

Figma: [Read only](https://www.figma.com/design/DD0eEumGwzGfrPbb7q2iak/-Zone--Tokens---Components?node-id=2148-378),
[Dismissible](https://www.figma.com/design/DD0eEumGwzGfrPbb7q2iak/-Zone--Tokens---Components?node-id=2200-674),
[Guidelines](https://www.figma.com/design/eOrg3dHaQFZ7VdCZCcomNC/-Zone-DS--Components-Specs?node-id=1522-27704)
</docs>

<template>
  <span :class="wrapperClasses">
    <ZoneIcon
      v-if="icon"
      :icon="icon"
      :color="color"
      :shade="iconShade"
      :size="sizeClasses.iconSize"
      :class="sizeClasses.leadingIcon"
    />
    <span :class="[sizeClasses.label, 'truncate']">
      <slot />
    </span>
    <ZoneIcon
      v-if="dismissible"
      icon="close"
      :color="color"
      :shade="closeIconShade"
      size="xs"
      :class="[sizeClasses.closeIcon, 'shrink-0 cursor-pointer']"
      aria-label="Remove"
      @click="$emit('dismiss')"
    />
  </span>
</template>

<script lang="ts">
import { computed, type PropType } from 'vue';
import ZoneIcon, { type IconShade, type IconName } from './Icon.vue';

const COLORS = ['primary', 'gray', 'red', 'orange', 'yellow', 'green', 'teal', 'indigo', 'purple', 'pink'] as const;

export type PillColor = (typeof COLORS)[number];

const SHAPES = ['round', 'square'] as const;

export type PillShape = (typeof SHAPES)[number];

const VARIANTS = ['filled', 'outlined'] as const;

export type PillVariant = (typeof VARIANTS)[number];

const SIZES = ['sm', 'md', 'lg'] as const;

export type PillSize = (typeof SIZES)[number];

const SHAPE_CLASSES: Record<PillShape, string> = {
  round: 'rounded-full',
  square: 'rounded-md',
};

interface PillSizeClasses {
  wrapper: string;
  text: string;
  label: string;
  leadingIcon: string;
  closeIcon: string;
  iconSize: 'xs' | 'lg';
}

// iconSize tracks the glyph Figma uses per size — bolt/12 at Small, bolt/18 at Medium and Large.
// An 18px glyph would overflow the 16px-tall small pill. The close (x) icon is 12px at every size.
const SIZE_CLASSES: Record<PillSize, PillSizeClasses> = {
  sm: { wrapper: 'py-0', text: 'text-xs leading-4', label: 'px-2', leadingIcon: 'pl-1.5', closeIcon: 'pr-1', iconSize: 'xs' },
  md: { wrapper: 'py-1', text: 'text-xs leading-4', label: 'px-2', leadingIcon: 'pl-1.5', closeIcon: 'pr-2', iconSize: 'lg' },
  lg: { wrapper: 'py-2', text: 'text-sm leading-5', label: 'px-3', leadingIcon: 'pl-2', closeIcon: 'pr-3', iconSize: 'lg' },
};

// Filled: bg-{color}-100 + a per-color text shade tuned for contrast against that background
// (ported 1:1 from the Figma "Pill - Read only" / "Pill - Dismissible" color variants).
const FILLED_CLASSES: Record<PillColor, string> = {
  primary: 'bg-primary-100 text-primary-800',
  gray: 'bg-gray-100 text-gray-600',
  red: 'bg-red-100 text-red-700',
  orange: 'bg-orange-100 text-orange-800',
  yellow: 'bg-yellow-100 text-yellow-600',
  green: 'bg-green-100 text-green-800',
  teal: 'bg-teal-100 text-teal-800',
  indigo: 'bg-indigo-100 text-indigo-800',
  purple: 'bg-purple-100 text-purple-800',
  pink: 'bg-pink-100 text-pink-800',
};

const OUTLINED_CLASSES: Record<PillColor, string> = {
  primary: 'border border-solid border-primary-700 text-primary-700',
  gray: 'border border-solid border-gray-600 text-gray-600',
  red: 'border border-solid border-red-700 text-red-700',
  orange: 'border border-solid border-orange-700 text-orange-700',
  yellow: 'border border-solid border-yellow-600 text-yellow-600',
  green: 'border border-solid border-green-600 text-green-600',
  teal: 'border border-solid border-teal-600 text-teal-600',
  indigo: 'border border-solid border-indigo-700 text-indigo-700',
  purple: 'border border-solid border-purple-600 text-purple-600',
  pink: 'border border-solid border-pink-600 text-pink-600',
};

// Numeric equivalents of the shades embedded in FILLED_CLASSES/OUTLINED_CLASSES above, so the
// leading ZoneIcon (which takes `color`/`shade` props rather than a class string) matches exactly.
const FILLED_ICON_SHADE: Record<PillColor, IconShade> = {
  primary: 800, gray: 600, red: 700, orange: 800, yellow: 600, green: 800, teal: 800, indigo: 800, purple: 800, pink: 800,
};

const OUTLINED_ICON_SHADE: Record<PillColor, IconShade> = {
  primary: 700, gray: 600, red: 700, orange: 700, yellow: 600, green: 600, teal: 600, indigo: 700, purple: 600, pink: 600,
};

// The close (x) icon always uses this shade regardless of `variant` — every sampled Figma
// "Pill - Dismissible" instance uses it on the close icon even when the label uses the filled
// shade. It tracks OUTLINED_ICON_SHADE for every colour except gray, which Figma drops to -500.
const CLOSE_ICON_SHADE: Record<PillColor, IconShade> = {
  primary: 700, gray: 500, red: 700, orange: 700, yellow: 600, green: 600, teal: 600, indigo: 700, purple: 600, pink: 600,
};
</script>

<script setup lang="ts">
defineOptions({ name: 'ZonePill' });

const props = defineProps({
  /** Figma calls `primary` "Blue". */
  color: {
    type: String as PropType<PillColor>,
    default: 'gray',
    validator: (v: string) => COLORS.includes(v as PillColor),
  },
  shape: {
    type: String as PropType<PillShape>,
    default: 'round',
    validator: (v: string) => SHAPES.includes(v as PillShape),
  },
  variant: {
    type: String as PropType<PillVariant>,
    default: 'filled',
    validator: (v: string) => VARIANTS.includes(v as PillVariant),
  },
  size: {
    type: String as PropType<PillSize>,
    default: 'sm',
    validator: (v: string) => SIZES.includes(v as PillSize),
  },
  /** Zone icon name for a leading icon (see ZoneIcon). */
  icon: {
    type: String as PropType<IconName>,
    default: null,
  },
  /**
   * Shows a trailing × icon that emits `dismiss`. The pill does not remove itself: the parent
   * removes it from its list.
   */
  dismissible: {
    type: Boolean,
    default: false,
  },
});

defineEmits<{
  /** × was clicked (needs `dismissible`). The parent removes the pill from its list. */
  dismiss: [];
}>();

defineSlots<{
  /** The pill label. */
  default?(): any;
}>();

const sizeClasses = computed<PillSizeClasses>(() => SIZE_CLASSES[props.size]);

const iconShade = computed<IconShade>(() => (
  props.variant === 'outlined' ? OUTLINED_ICON_SHADE[props.color] : FILLED_ICON_SHADE[props.color]
));

const closeIconShade = computed<IconShade>(() => CLOSE_ICON_SHADE[props.color]);

const wrapperClasses = computed<string>(() => {
  const colorClasses = props.variant === 'outlined' ? OUTLINED_CLASSES[props.color] : FILLED_CLASSES[props.color];

  return [
    'inline-flex items-center',
    SHAPE_CLASSES[props.shape],
    sizeClasses.value.wrapper,
    sizeClasses.value.text,
    colorClasses,
  ].join(' ');
});
</script>
